import React, { useState } from "react";
import { Box } from "@mui/material";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { REQUEST_LIST_GRID_COMPACT_SX } from "../constants";
import { dateTimeFormatter } from "src/utils/date";
import { GridColDef } from "@mui/x-data-grid-premium";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import { useTranslate } from "src/locales";
import type { RequestDetailRow } from "../types";
import EditNotesModal from "./modals/EditNotesModal";
import WarningModal from "src/components/shared-components/modal/WarningModal";
import PackageActionModal from "./modals/PackageActionModal";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import { requestService } from "src/api/request/requestService";
import type { PackageAction } from "src/api/request/requestService";
import { useSnackbar } from "notistack";

export type RequestDetailsMenuVariant = "request-details" | "out-of-request";

interface RequestDetailsGridProps {
  rows: RequestDetailRow[];
  loading?: boolean;
  menuVariant?: RequestDetailsMenuVariant;
  onRefresh?: () => void;
  currentUser?: string;
  ptId?: string | number;
  title?: string;
}

export default function RequestDetailsGrid({
  rows,
  loading = false,
  title,
  menuVariant = "request-details",
  onRefresh,
  currentUser = "",
  ptId,
}: RequestDetailsGridProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [localRows, setLocalRows] = useState<RequestDetailRow[]>(rows);

  React.useEffect(() => {
    setLocalRows(rows);
  }, [rows]);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RequestDetailRow | null>(null);

  const handleEditClick = (row: RequestDetailRow) => {
    setSelectedRow(row);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedRow(null);
  };

  const handleEditSuccess = (updatedNote: string, updatedCrNote: string) => {
    if (selectedRow) {
      setLocalRows(prev => prev.map(row => 
        row.id === selectedRow.id 
          ? { ...row, note: updatedNote, consultationNote: updatedCrNote }
          : row
      ));
    }
  };

  // --- Pending cancel flow ---
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [rowToCancel, setRowToCancel] = useState<RequestDetailRow | null>(null);

  const handleCancelClick = (row: RequestDetailRow) => {
    setRowToCancel(row);
    setWarningModalOpen(true);
  };

  const handleWarningModalClose = () => {
    setWarningModalOpen(false);
    setRowToCancel(null);
  };

  const handleConfirmCancel = async (note: string) => {
    if (!rowToCancel) return;

    try {
      const response = await requestService.cancelRequestDetail({
        prdId: Number(rowToCancel.id),
        cancelNote: note,
        cancelUser: currentUser,
      });

      if (response.issuccess) {
        enqueueSnackbar(t("common.success.cancel", "default", { defaultValue: "İşlem başarıyla iptal edildi." }), { variant: "success" });
        onRefresh?.();
        handleWarningModalClose();
      } else {
        enqueueSnackbar(response.message || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }), { variant: "error" });
      }
    } catch (error) {
       console.error("Cancel request detail error:", error);
       enqueueSnackbar(t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }), { variant: "error" });
    }
  };

  // --- Approved cancel flow ---
  const [approvedWarningOpen, setApprovedWarningOpen] = useState(false);
  const [rowToCancelApproved, setRowToCancelApproved] = useState<RequestDetailRow | null>(null);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [pendingCancelNote, setPendingCancelNote] = useState("");

  const handleCancelApprovedClick = (row: RequestDetailRow) => {
    setRowToCancelApproved(row);
    setApprovedWarningOpen(true);
  };

  const handleApprovedWarningClose = () => {
    setApprovedWarningOpen(false);
    setRowToCancelApproved(null);
    setPendingCancelNote("");
  };

  const callCancelApprovedDetailApi = async (
    row: RequestDetailRow,
    note: string,
    packageAction?: PackageAction | null
  ) => {
    try {
      const response = await requestService.cancelApprovedRequestDetail({
        prdId: Number(row.id),
        ptId: Number(ptId),
        cancelNote: note,
        packageAction: packageAction || null,
      });

      if (response.issuccess) {
        enqueueSnackbar(
          t("common.success.cancel", "default", { defaultValue: "İşlem başarıyla iptal edildi." }),
          { variant: "success" }
        );
        onRefresh?.();
        handleApprovedWarningClose();
        setPackageModalOpen(false);
      } else {
        enqueueSnackbar(
          response.message || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }),
          { variant: "error" }
        );
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.errorCode || error?.response?.data?.ErrorCode;
      const errorMessage = error?.response?.data?.message || error?.response?.data?.Message;

      if (errorCode === "DELETE_PROCESS_025") {
        setPendingCancelNote(note);
        setApprovedWarningOpen(false);
        setPackageModalOpen(true);
      } else {
        console.error("Cancel approved request detail error:", error);
        enqueueSnackbar(
          errorMessage || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }),
          { variant: "error" }
        );
      }
    }
  };

  const handleConfirmCancelApproved = async (note: string) => {
    if (!rowToCancelApproved) return;
    await callCancelApprovedDetailApi(rowToCancelApproved, note);
  };

  const handlePackageActionSelect = async (action: PackageAction) => {
    if (action === "Cancel") {
      setPackageModalOpen(false);
      return;
    }
    if (!rowToCancelApproved) return;
    await callCancelApprovedDetailApi(rowToCancelApproved, pendingCancelNote, action);
  };

  // --- Menu items ---
  const getMenuItems = React.useCallback((row: RequestDetailRow) => {
    const translate = (key: string, fallback: string) =>
      t(key, "default", { defaultValue: fallback }) as string;

    const makeActiveItem = (labelKey: string, fallback: string, onClick?: () => void, divider?: boolean, disabled?: boolean) => ({
      label: translate(labelKey, fallback),
      onClick: onClick || (() => {}),
      divider,
      disabled,
    });

    const makeDisabledItem = (labelKey: string, fallback: string, divider?: boolean) => ({
      label: `${translate(labelKey, fallback)} (Geliştiriliyor)`,
      onClick: () => {},
      disabled: true,
      divider,
    });

    if (menuVariant === "out-of-request") {
      return [
        makeDisabledItem("request-list.menu.put-on-hold-pacs", "Beklemeye Al(PACS)"),
        makeDisabledItem("request-list.menu.show-images", "Resimleri Göster", true),
      ];
    }
    
    return [
      makeActiveItem(
        "request-list.menu.cancel",
        "İptal",
        () => handleCancelClick(row),
        false,
        row.requestStatus === "Onaylandı" 
      ),
      makeActiveItem(
        "request-list.menu.cancel-approved",
        "İptal(Onaylanmışlar)",
        () => handleCancelApprovedClick(row),
        true,
        row.requestStatus !== "Onaylandı" 
      ),
      makeDisabledItem("request-list.menu.put-on-hold-pacs", "Beklemeye Al(PACS)"),
      makeDisabledItem("request-list.menu.show-images", "Resimleri Göster"),
      makeDisabledItem("request-list.menu.open-consultation-card", "Konsültasyon Kartı Aç"),
      makeDisabledItem("request-list.menu.open-consultation", "Konsültasyon Aç"),
      makeDisabledItem("request-list.menu.change-pathology-request-info", "Patoloji İstek Bilgilerini Değiştir"),
      makeDisabledItem("request-list.menu.print-pathology-barcode", "Patoloji Barkodunu Yazdır", true),
    ];
  }, [t, menuVariant]);

  const columns: GridColDef<RequestDetailRow>[] = [
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ByzMuiOpenProcessMenu
          hideHeaderTitle
          menuItems={getMenuItems(params.row)}
          onClose={() => {}}
        />
      ),
    },
    {
      field: "actionName",
      headerName: t("request-list.columns.action-name", "default", {
        defaultValue: "İşlem Adı",
      }),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "note",
      headerName: t("request-list.columns.note", "default", {
        defaultValue: "Not",
      }),
      width: 100,
    },
    {
      field: "quantity",
      headerName: t("request-list.columns.quantity", "default", {
        defaultValue: "Sayı",
      }),
      width: 80,
      type: "number",
    },
    {
      field: "requestStatus",
      headerName: t("request-list.columns.request-status", "default", {
        defaultValue: "İstek Durumu",
      }),
      width: 120,
    },
    {
      field: "actionStatus",
      headerName: t("request-list.columns.action-status", "default", {
        defaultValue: "İşlem Durumu",
      }),
      width: 120,
    },
    {
      field: "pacsStatus",
      headerName: t("request-list.columns.pacs-status", "default", {
        defaultValue: "PACS Durumu",
      }),
      width: 120,
    },
    {
      field: "consultationNote",
      headerName: t("request-list.columns.consultation-note", "default", {
        defaultValue: "Konsültasyon Notu",
      }),
      flex: 1,
      minWidth: 140,
    },
    {
      field: "actionDate",
      headerName: t("request-list.columns.action-date", "default", {
        defaultValue: "İşlem Tarihi",
      }),
      width: 150,
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm:ss"),
    },
    {
      field: "performedBy",
      headerName: t("request-list.columns.performed-by", "default", {
        defaultValue: "İşlem Yapan",
      }),
      width: 100,
    },
    {
      field: "requestDate",
      headerName: t("request-list.columns.request-date", "default", {
        defaultValue: "İstek Tarihi",
      }),
      width: 150,
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm:ss"),
    },
  ];

  // Add edit column only for request-details variant and at the end
  if (menuVariant === "request-details") {
    columns.push({
      field: "edit",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ByzGrdEditButton
            onClick={() => handleEditClick(params.row)}
          />
        </Box>
      ),
    });
  }

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      {title && (
        <Box sx={{ flexShrink: 0, minHeight: 0, px: 2 }}>
          <ByzMuiTypography
            isTitle
            component="div"
            sx={{ fontSize: 20, mb: 0.5, color: "#111827" }}
          >
            {title}
          </ByzMuiTypography>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 0,
              mx: -2,
            }}
          />
        </Box>
      )}
      <Box sx={{ px: 2 }}>
      <ByzMuiDataGrid
        rows={localRows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10]}
        initialState={{ 
          density: "compact",
          pagination: { paginationModel: { pageSize: 10 } }
        }}
        loading={loading}
        autoHeight
        sx={{ marginTop: 0, ...REQUEST_LIST_GRID_COMPACT_SX }}
        customToolbar={() => (
          <ByzDataGridToolbar
            searchPlaceholder={t("request-list.search", "default", { defaultValue: "Ara..." })}
            searchIconColor="#1E88E5"
          />
        )}
        whiteGrid
        headerWhite
        getRowId={(row) => row.id}
      />
      </Box>

      {/* Edit Notes Modal */}
      <EditNotesModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        prdId={selectedRow?.id || 0}
        actionName={selectedRow?.actionName || ""}
        initialNote={selectedRow?.note || ""}
        initialCrNote={selectedRow?.consultationNote || ""}
        onSuccess={handleEditSuccess}
      />

      {/* Pending cancel warning modal */}
      <WarningModal
        open={warningModalOpen}
        onClose={handleWarningModalClose}
        onConfirm={handleConfirmCancel}
        message={t("request-list.warning.cancel-confirm", "default", { defaultValue: "Seçili istek iptal edilecek, emin misiniz?" })}
        hasReadyMadeText={false}
      />

      {/* Approved cancel warning modal */}
      <WarningModal
        open={approvedWarningOpen}
        onClose={handleApprovedWarningClose}
        onConfirm={handleConfirmCancelApproved}
        message={t("request-list.warning.cancel-approved-confirm", "default", { defaultValue: "Onaylanmış istek detayı iptal edilecek, emin misiniz?" })}
        hasReadyMadeText={false}
      />

      {/* Package action selection modal (DELETE_PROCESS_025) */}
      <PackageActionModal
        open={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        onSelect={handlePackageActionSelect}
      />
    </Box>
  );
}
